import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Users,
  Layout,
  Code,
  Shield,
  Globe,
  PieChart,
} from "lucide-react";

const ProcessSection = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

 const whatWeOffer = [
  {
    icon: Globe,
    title: "Complete Data Cleaning & Standardization",
    description:
      "Remove duplicates, fix inconsistencies, and standardize formats to ensure clean, accurate, and reliable datasets.",
  },
  {
    icon: Layout,
    title: "Seamless Data Migration Across Platforms",
    description:
      "Migrate data between legacy systems, CRMs, ERPs, databases, or cloud platforms with zero loss and full integrity.",
  },
  {
    icon: Users,
    title: "Data Mapping & Transformation",
    description:
      "Advanced schema mapping and data restructuring to align old formats with new system requirements.",
  },
  {
    icon: PieChart,
    title: "Data Quality Validation & Error Checks",
    description:
      "Comprehensive validation to ensure completeness, accuracy, and consistency across all datasets.",
  },
  {
    icon: Code,
    title: "Database Optimization & Consolidation",
    description:
      "Merge datasets, optimize tables, and improve database performance for faster insights and applications.",
  },
  {
    icon: Shield,
    title: "Secure Backup, Recovery & Compliance",
    description:
      "Encrypted backups, rollback systems, and compliance-aligned handling to protect sensitive information.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Data Audit & Requirement Analysis",
    description:
      "We assess data sources, formats, quality issues, and migration goals to plan the right cleaning and transfer strategy.",
    icon: Users,
      imagePath: "./images/servicesImages/Data Audit & Requirement Analysis.png",
  },
  {
    number: "02",
    title: "Data Cleaning & Pre-Processing",
    description:
      "Duplicates, errors, missing values, and outdated formats are corrected to ensure clean, high-quality datasets.",
    icon: Layout,
      imagePath: "./images/servicesImages/Data Cleaning & Pre-Processing.png",
  },
  {
    number: "03",
    title: "Migration Execution & Quality Checks",
    description:
      "We securely migrate data across systems, validate mapping accuracy, and verify integrity after transfer.",
    icon: Code,
      imagePath: "./images/servicesImages/Migration Execution & Quality Checks.png",
  },
  {
    number: "04",
    title: "Post-Migration Verification & Support",
    description:
      "We perform final checks, optimize structures, resolve inconsistencies, and provide continuous support.",
    icon: Shield,
      imagePath: "./images/servicesImages/Post-Migration Verification & Support.png",
  },
];


const ourEdge = [
  "🧹 100% Clean & Verified Data: Advanced cleaning rules ensure consistent, accurate, and error-free datasets.",
  "🔐 Secure & Compliant Migration: Encrypted transfers and best-practice protocols protect sensitive information.",
  "⚙️ Zero-Loss Data Migration: Robust backups and validation workflows guarantee safe and complete transfers.",
  "📈 Scalable Across Systems: Works seamlessly with CRMs, ERPs, SQL, NoSQL, Cloud, APIs, and large-scale datasets.",
];


const howWeWork = [
  "1. Data Assessment & Planning – We analyze sources, structure, and quality to design the right cleaning and migration plan.",
  "2. Cleaning & Standardization – Data is cleaned, formatted, validated, and prepared for accurate migration.",
  "3. Migration & Verification – We securely migrate data and verify structure, consistency, and completeness.",
  "4. Post-Migration Support – We optimize performance, fix discrepancies, and ensure smooth system integration.",
];


  return (
    <section className="py-20 px-[5%] bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* What We Offer Section */}
        <div className="mb-28">
          <h2
            className="text-center text-3xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-12"
            data-aos="fade-up"
          >
            What We Offer
          </h2>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {whatWeOffer.map((feature, index) => (
              <div
                key={index}
                className="bg-[#dcfaf9] rounded-full p-8 shadow-md border-t-4 border-b-4 border-[#0d6b68] hover:shadow-xl transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="flex items-center gap-4 mb-4">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-32" data-aos="fade-up">
          <h2 className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-6">
            Our Process
          </h2>
          <p className="text-gray-600 text-lg sm:text-xl max-w-2xl mx-auto">
            We follow a structured, step-by-step approach to ensure smooth
            deployment with zero disruptions. From environment setup to final
            rollout, everything is optimized, tested, and delivered ready for
            real-world use.
          </p>
        </div>

        {/* Process Grid */}
        <div className="grid md:grid-cols-2 gap-y-32 gap-x-10">
          {processSteps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="flex flex-col relative"
                data-aos="fade-up"
                data-aos-delay={index * 200}
              >
                {/* Background Number */}
                <div className="absolute -top-24 text-6xl md:text-9xl font-bold text-[#9ee8e7] select-none pointer-events-none z-0">
                  {step.number}
                </div>

                {/* Card */}
                <div className="bg-gradient-to-br from-[#0d6b68] to-[#baf7f6] rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 z-10 border border-gray-100">
                  {/* Card Image/Icon Area */}
                  <div className="mb-8 relative">
                    <div className="w-full h-fit bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl flex items-center justify-center overflow-hidden relative">
                      <img
                        src={step.imagePath}
                        alt={step.title}
                        className="w-full h-38 md:h-40 lg:h-52 object-cover rounded-2xl"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />

                      {/* Fallback Icon */}
                      <div className="absolute inset-0 hidden items-center justify-center">
                        <div className="relative">
                          <div className="absolute -top-4 -left-4 w-3 h-3 bg-blue-400 rounded-full opacity-60"></div>
                          <div className="absolute -bottom-6 -right-6 w-2 h-2 bg-purple-400 rounded-full opacity-40"></div>
                          <div className="absolute top-2 right-4 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-50"></div>
                          <div className="bg-white p-6 rounded-2xl shadow-lg">
                            <IconComponent className="w-12 h-12 text-blue-600" />
                          </div>
                          <div className="absolute top-1/2 -left-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute top-1/2 -right-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute -top-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                          <div className="absolute -bottom-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Our Edge Section */}
        <div className="text-center mt-32" data-aos="fade-up">
          <h2
            className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-8"
            style={{ lineHeight: "1.4" }}
          >
            Our Edge
          </h2>

          <ul className="text-gray-700 max-w-4xl mx-auto space-y-4 text-lg">
            {ourEdge.map((point, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {point}
              </li>
            ))}
          </ul>
        </div>

        {/* How We Work Section */}
        <div className="text-center mt-20" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            How We Work
          </h2>
          <ul className="text-gray-700 max-w-3xl mx-auto space-y-4 text-lg">
            {howWeWork.map((step, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {step}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
